import type { SignDocumentPayload } from "@/api/user-agreement-document";
import BrandedSuccessLottie from "@/components/ui/BrandedSuccessLottie";
import { Button } from "@/components/ui/button/button";
import { FormField } from "@/components/ui/form/form";

import Typography from "@/components/ui/typography";
import { cn } from "@/lib/utils";
import { zodResolver } from "@hookform/resolvers/zod";
import { useLoaderData } from "@tanstack/react-router";
import { AlertCircle } from "lucide-react";
import { useRef, useState } from "react";
import { useForm } from "react-hook-form";
import SignatureCanvas from "react-signature-canvas";
import { z } from "zod";
import FloatingInput from "../ui/floatingInput";
import SubmitButton from "../ui/submit-button";
import { getTodayYYYYMMDD } from "./user-agreement-signing.utils";

/** ISO date: YYYY-MM-DD (MySQL-friendly). */
const YYYY_MM_DD_REGEX = /^\d{4}-\d{2}-\d{2}$/;

/** Parse YYYY-MM-DD string to Date (month is 0-indexed in JS). */
function parseYYYYMMDD(value: string): Date {
	const [year, month, day] = value.split("-").map(Number);
	return new Date(year, month - 1, day);
}

const agreementSigningSchema = z.object({
	fullName: z.string().trim().min(3, "Please enter your full name."),
	signerDate: z
		.string()
		.min(1, "Please enter the date.")
		.regex(YYYY_MM_DD_REGEX, "Please enter the date as YYYY-MM-DD.")
		.refine(
			(value) => {
				const entered = parseYYYYMMDD(value);
				const today = new Date();
				today.setHours(0, 0, 0, 0);
				entered.setHours(0, 0, 0, 0);
				return entered.getTime() <= today.getTime();
			},
			{ message: "The date cannot be in the future." },
		),
});

type AgreementSigningFormValues = z.infer<typeof agreementSigningSchema>;

export interface UserAgreementSigningProps {
	onContinueClick: () => void;
	locationId: number;
	documentId: number;
	onSubmitSigning: (payload: SignDocumentPayload) => void;
	isSubmitting: boolean;
	isSuccess: boolean;
}

type UserAgreementSteps = "sign" | "name" | "date";

const AGREEMENT_STEPS: UserAgreementSteps[] = ["sign", "name", "date"];

function getDefaultFullName(
	firstName: string | undefined,
	lastName: string | undefined,
): string {
	if (firstName != null && lastName != null) {
		return `${firstName} ${lastName}`.trim();
	}
	if (firstName != null) return firstName.trim();
	if (lastName != null) return lastName.trim();
	return "";
}

export function UserAgreementSigning({
	onContinueClick,
	documentId,
	locationId,
	onSubmitSigning,
	isSubmitting,
	isSuccess,
}: UserAgreementSigningProps) {
	const signatureRef = useRef<SignatureCanvas>(null);
	const { user } = useLoaderData({ from: "/main" });
	const defaultFullName = getDefaultFullName(user?.first_name, user?.last_name);

	const form = useForm<AgreementSigningFormValues>({
		resolver: zodResolver(agreementSigningSchema),
		defaultValues: {
			fullName: defaultFullName,
			signerDate: getTodayYYYYMMDD(),
		},
	});

	const [signatureBlob, setSignatureBlob] = useState<Blob | null>(null);
	const [error, setError] = useState<string | null>(null);
	const [step, setStep] = useState<UserAgreementSteps>("sign");

	const isSubmittingOrPending = isSubmitting;

	const handleClear = () => {
		signatureRef.current?.clear();
		setError(null);
	};

	// Step 1: signature → Next. Step 2: name → Back/Next. Step 3: date → Back/Sign and continue.
	// In drawer flow the canvas is unmounted on steps 2–3, so we capture the blob here for submit.
	const handleSignStepNext = () => {
		setError(null);
		const canvas = signatureRef.current;
		if (!canvas) {
			setError("Signature pad is not ready. Please try again.");
			return;
		}
		if (canvas.isEmpty()) {
			setError("Please sign in the box above before continuing.");
			return;
		}
		const canvasEl = canvas.getCanvas();
		if (!canvasEl) {
			setError("Could not capture signature. Please try again.");
			return;
		}
		canvasEl.toBlob(
			(blob) => {
				if (!blob) {
					setError("Could not capture signature. Please try again.");
					return;
				}
				setSignatureBlob(blob);
				setStep("name");
			},
			"image/png",
			1,
		);
	};

	const handleDateStepSubmit = async () => {
		setError(null);
		const isValid = await form.trigger("signerDate");
		if (!isValid) {
			return;
		}
		const values = form.getValues();
		let blob: Blob | null = signatureBlob ?? null;
		if (!blob) {
			const canvas = signatureRef.current?.getCanvas();
			if (!canvas) {
				setError("Signature is not ready. Please try again.");
				return;
			}
			blob = await new Promise<Blob | null>((resolve) => {
				canvas.toBlob(resolve, "image/png");
			});
		}
		if (!blob) {
			setError("Could not create signature image. Please try again.");
			return;
		}
		const payload: SignDocumentPayload = {
			signerFullName: values.fullName,
			signerDate: values.signerDate,
			signature: blob,
			documentId,
			locationId,
		};
		onSubmitSigning(payload);
	};

	const handleNameStepNext = async () => {
		setError(null);
		const isValid = await form.trigger("fullName");
		if (!isValid) {
			return;
		}
		setStep("date");
	};

	const handleNameStepBack = () => {
		setError(null);
		setSignatureBlob(null);
		setStep("sign");
	};

	const handleDateStepBack = () => {
		setError(null);
		setStep("name");
	};

	// Multi-step drawer: signature → full name → date. After submit: success view.
	const drawerStepContent = (
		<div className="flex flex-col gap-4 px-4 pb-4 flex-1 min-h-0 overflow-hidden">
			<div className="flex justify-center my-4 space-x-2">
				{(() => {
					const currentStepIndex = AGREEMENT_STEPS.indexOf(step);
					return AGREEMENT_STEPS.map((s, index) => (
						<Button
							key={s}
							size="compact"
							className={cn(
								"w-2 h-2 rounded-full transition-colors",
								index <= currentStepIndex ? "bg-primary-600" : "bg-primary-200",
							)}
						/>
					));
				})()}
			</div>
			{isSuccess ? (
				<div className="flex flex-col items-center justify-center flex-1 min-h-0 gap-4 text-center">
					<div className="">
						<BrandedSuccessLottie
							style={{
								maxWidth: 150,
							}}
						/>
					</div>
					<div className="">
						<Typography variant="h2" className="text-textDark">
							Document has been signed!
						</Typography>
						<Typography variant="p" color="primary" className="px-2">
							Your document was successfully signed and you can now continue
							with your transfer
						</Typography>
					</div>
					<Button
						type="button"
						className="w-full mt-8"
						onClick={onContinueClick}
						data-testid="agreement-success-continue"
					>
						Continue
					</Button>
				</div>
			) : (
				<>
					{step === "sign" && (
						<>
							<div className="space-y-2">
								<Typography variant="h5" className="text-textDark">
									Signature
								</Typography>
								<div
									className="rounded-lg w-full border-2 border-input bg-white overflow-hidden touch-none"
									data-testid="signature-canvas"
									onPointerDown={(e) => e.stopPropagation()}
									onPointerMove={(e) => e.stopPropagation()}
									onPointerUp={(e) => e.stopPropagation()}
									onTouchStart={(e) => e.stopPropagation()}
									onTouchMove={(e) => e.stopPropagation()}
									onTouchEnd={(e) => e.stopPropagation()}
								>
									<SignatureCanvas
										ref={signatureRef}
										penColor="#0f172a"
										canvasProps={{
											className: "w-full h-[120px] sm:h-[140px] touch-none",
										}}
										clearOnResize={false}
									/>
								</div>
								<Button
									type="button"
									variant="outline"
									size="sm"
									onClick={handleClear}
								>
									Clear
								</Button>
							</div>
							{error && (
								<div
									className="flex items-center gap-2 rounded-md border border-destructive/50 bg-destructive/10 px-3 py-2 text-sm text-destructive"
									role="alert"
								>
									<AlertCircle className="w-4 h-4 flex-shrink-0" />
									<span>{error}</span>
								</div>
							)}
							<div className="flex gap-2">
								<Button
									type="button"
									className="w-full"
									onClick={handleSignStepNext}
									data-testid="agreement-step1-next"
								>
									Next
								</Button>
							</div>
						</>
					)}

					{step === "name" && (
						<>
							<FormField
								control={form.control}
								name="fullName"
								render={({ field }) => (
									<FloatingInput
										label="Full Name"
										type="text"
										data-testid="agreement-full-name"
										{...field}
										error={form.formState.errors.fullName}
									/>
								)}
							/>

							{error && (
								<div
									className="flex items-center gap-2 rounded-md border border-destructive/50 bg-destructive/10 px-3 py-2 text-sm text-destructive"
									role="alert"
								>
									<AlertCircle className="w-4 h-4 flex-shrink-0" />
									<span>{error}</span>
								</div>
							)}
							<div className="flex gap-2">
								<Button
									type="button"
									variant="outline"
									className="w-full"
									onClick={handleNameStepBack}
									disabled={isSubmittingOrPending}
									data-testid="agreement-step2-back"
								>
									Back
								</Button>
								<Button
									type="button"
									className="w-full"
									onClick={handleNameStepNext}
									data-testid="agreement-step2-next"
								>
									Next
								</Button>
							</div>
						</>
					)}

					{step === "date" && (
						<>
							<FormField
								control={form.control}
								name="signerDate"
								render={({ field }) => (
									<FloatingInput
										label="Date (YYYY-MM-DD)"
										type="text"
										placeholder="YYYY-MM-DD"
										inputMode="numeric"
										autoComplete="off"
										data-testid="agreement-date"
										{...field}
										error={form.formState.errors.signerDate}
									/>
								)}
							/>

							{error && (
								<div
									className="flex items-center gap-2 rounded-md border border-destructive/50 bg-destructive/10 px-3 py-2 text-sm text-destructive"
									role="alert"
								>
									<AlertCircle className="w-4 h-4 flex-shrink-0" />
									<span>{error}</span>
								</div>
							)}
							<Typography
								variant="p"
								color="primary-light"
								className="flex justify-center"
							>
								By clicking “sign and complete”, you agree to the terms and
								conditions
							</Typography>
							<div className="flex gap-2">
								<Button
									type="button"
									variant="outline"
									className="w-full"
									onClick={handleDateStepBack}
									disabled={isSubmittingOrPending}
									data-testid="agreement-step3-back"
								>
									Back
								</Button>
								<SubmitButton
									type="button"
									className="w-full"
									onClick={handleDateStepSubmit}
									disabled={isSubmittingOrPending}
									mutationStatus={isSubmitting ? "pending" : "idle"}
									loadingText="Signing..."
									testId="agreement-sign-submit"
								>
									Sign and Complete
								</SubmitButton>
							</div>
						</>
					)}
				</>
			)}
		</div>
	);

	return <div className={cn("flex flex-col min-h-0")}>{drawerStepContent}</div>;
}
